import { useMemo } from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import { Clock, ArrowRight } from "lucide-react";
import { ArticleCard } from "./ArticleCard";
import type { Article } from "@/data/articles";
import type { CategoryRow } from "@/hooks/useCategories";

interface RecentArticlesSectionProps {
  articles: Article[];
  categories: CategoryRow[];
  getByCategory: (slug: string) => Article[];
}

export function RecentArticlesSection({
  articles,
  categories,
  getByCategory,
}: RecentArticlesSectionProps) {
  // Build a curated recent mix: 1 most-recent article per category, up to 8 total.
  // Falls back to the globally newest articles to fill any gaps.
  const recentMix = useMemo(() => {
    const picked = new Set<string>();
    const result: Article[] = [];

    // One from each category (in sort_order)
    for (const cat of categories) {
      if (result.length >= 8) break;
      const catArticles = getByCategory(cat.slug);
      const first = catArticles.find((a) => !picked.has(a.id));
      if (first) {
        picked.add(first.id);
        result.push(first);
      }
    }

    // Fill remaining slots from global newest
    if (result.length < 8) {
      for (const a of articles) {
        if (result.length >= 8) break;
        if (!picked.has(a.id)) {
          picked.add(a.id);
          result.push(a);
        }
      }
    }

    // Re-sort mix by date descending (articles array is already sorted; this re-sorts the mix)
    return result.sort((a, b) => {
      const da = new Date(a.date || 0).getTime();
      const db = new Date(b.date || 0).getTime();
      return db - da;
    });
  }, [articles, categories, getByCategory]);

  if (recentMix.length === 0) return null;

  return (
    <section className="mb-12">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Clock className="h-8 w-8 md:h-10 md:w-10 text-primary shrink-0" />
          <div className="flex flex-col justify-center min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-primary font-bold">/</span>
              <h2 className="text-lg md:text-xl font-bold text-foreground leading-tight truncate">
                Recent Stories
              </h2>
              <span className="text-primary font-bold">/</span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
              A fresh mix from every category
            </p>
          </div>
        </div>

        <Link
          to="/recent-articles"
          className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 group transition-colors"
        >
          View All
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
        </Link>
      </div>

      {/* Grid: first card is large (overlay), rest are standard */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      >
        {recentMix.length === 1 ? (
          <ArticleCard
            article={recentMix[0]}
            variant="overlay"
            className="aspect-video"
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {/* Featured (first article — larger span) */}
            <div className="md:col-span-2 lg:col-span-2">
              <ArticleCard
                article={recentMix[0]}
                variant="overlay"
                className="h-full min-h-[280px]"
                showBookmark
                priority
              />
            </div>

            {/* Remaining articles — full width 2-col on tablets, 2-span on desktop */}
            <div className="md:col-span-2 lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {recentMix.slice(1, 5).map((article) => (
                <ArticleCard
                  key={article.id}
                  article={article}
                  showExcerpt={false}
                />
              ))}
            </div>
          </div>
        )}

        {/* Second row for extra articles */}
        {recentMix.length > 5 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
            {recentMix.slice(5, 8).map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        )}
      </motion.div>
    </section>
  );
}
